/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag React.Fragment
 */
/**
 * BrowseSpacesFullscreen
 * ----------------------
 *
 * Fullscreen "View all spaces" page used by Rovo Chat. Mirrors the fullscreen
 * branch of `ChatConversationListRefresh` (the existing "View all chats"
 * layout in `conversation-assistant-ui-components`) so the two pages feel
 * visually consistent (same wrapper padding, same header rhythm, same row
 * density).
 *
 * Layout:
 *   - Header: page title + primary "Create space" CTA.
 *   - Search input backed by server-side spaces search.
 *   - Flat list of space rows. Clicking a row fires `onSelectSpace(space)`.
 *
 * Gating: this component is gated by the `rovo_chat_spaces_experiment`
 * experiment; callers are responsible for only rendering it when the experiment
 * is on. As defence-in-depth, we also gate the internal data fetch (via
 * `useSearchSpaces`'s `skip` option) when the experiment is off so we don't
 * make a `GET /spaces` request from a host that accidentally mounts us.
 *
 * Out of scope (delivered in FE-B5b / FE-B5c):
 *   - Townsquare route + view registration (FE-B5b).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { ResourcedEmoji } from '@atlaskit/emoji/element';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import FolderClosedIcon from '@atlaskit/icon/core/folder-closed';
import NodeIcon from '@atlaskit/icon/core/node';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type AssistanceServiceInputParam } from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import { ChatItem } from '@atlassian/conversation-assistant-ui-components/ui/chat-conversation-list-refresh/ChatItem';
import { SearchInput } from '@atlassian/conversation-assistant-ui-components/ui/SearchInput';

import { useSpacesAnalytics } from '../../analytics/useSpacesAnalytics';
import { createEmojiProvider } from '../../common/utils/EmojiProviderUtils';
import { useSpaceStoreActions } from '../../controllers/space-store/hooks';
import type { CreateSpaceResponse, Space } from '../../controllers/space-store/types';
import { useDeleteSpace } from '../../services/useDeleteSpace/useDeleteSpace';
import { useGetSpaceConversations } from '../../services/useGetSpaceConversations/useGetSpaceConversations';
import { useListSpaces } from '../../services/useListSpaces/useListSpaces';
import { useRenameSpace } from '../../services/useRenameSpace/useRenameSpace';
import { useSearchSpaces } from '../../services/useSearchSpaces/useSearchSpaces';
import type { RovoNavigationConversation } from '../../types/rovo-navigation-conversation';
import { CreateSpaceModal } from '../create-space-modal/CreateSpaceModal';
import { DeleteSpaceModal } from '../delete-space-modal/DeleteSpaceModal';

import noResultIconUrl from './assets/no_result.svg';
import messages from './messages';
import { NoSpacesEmptyState } from './NoSpacesEmptyState';
import { SpaceRowActions } from './SpaceRowActions';
import { SpaceRowRenameForm } from './SpaceRowRenameForm';
import { useInlineSpaceRename } from './useInlineSpaceRename';
import { useSearchSpaceConversations } from './useSearchSpaceConversations';

const ROW_MIN_HEIGHT = '64px';

/* eslint-disable @atlaskit/ui-styling-standard/no-unsafe-values -- fixed Figma content/list/icon dimensions */
const styles = cssMap({
	wrapper: {
		width: '100%',
	},
	sidebarWrapper: {
		overflowX: 'hidden',
		height: '100%',
		paddingBottom: token('space.200'),
	},
	fullscreenWrapper: {
		overflowX: 'hidden',
		overflowY: 'hidden',
		height: '100%',
	},
	fullscreenContentContainer: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		width: '100%',
		maxWidth: '800px',
		height: '100%',
		minHeight: '0px',
		marginInlineStart: 'auto',
		marginInlineEnd: 'auto',
		paddingTop: token('space.500'),
	},
	headerContainerBase: {
		gap: token('space.200'),
	},
	sidebarHeaderContainer: {
		paddingTop: token('space.250'),
		paddingBottom: token('space.250'),
		paddingInline: token('space.300'),
	},
	header: {
		justifyContent: 'space-between',
	},
	searchBarContainer: {
		display: 'flex',
		marginTop: token('space.200'),
	},
	listContainerBase: {
		overflowX: 'hidden',
		overflowY: 'auto',
		display: 'flex',
		flexDirection: 'column',
		maxHeight: '100%',
		minHeight: '0px',
		marginTop: token('space.300'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
	},
	searchResultsContainer: {
		rowGap: token('space.150'),
		columnGap: token('space.150'),
		borderTopWidth: 0,
		borderRightWidth: 0,
		borderBottomWidth: 0,
		borderLeftWidth: 0,
		backgroundColor: 'transparent',
	},
	searchResultItem: {
		borderTopWidth: token('border.width'),
		borderRightWidth: token('border.width'),
		borderBottomWidth: token('border.width'),
		borderLeftWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderRightStyle: 'solid',
		borderBottomStyle: 'solid',
		borderLeftStyle: 'solid',
		borderTopColor: token('color.border'),
		borderRightColor: token('color.border'),
		borderBottomColor: token('color.border'),
		borderLeftColor: token('color.border'),
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
	},
	sidebarListContainer: {
		marginTop: token('space.050'),
		marginInlineStart: token('space.300'),
		marginInlineEnd: token('space.300'),
	},
	spinnerContainer: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		paddingBlock: token('space.500'),
		paddingInline: token('space.150'),
	},
	rowLayout: {
		display: 'flex',
		alignItems: 'center',
		minHeight: ROW_MIN_HEIGHT,
	},
	rowContentGap: {
		gap: token('space.150'),
	},
	rowContainerGap: {
		gap: token('space.100'),
	},
	rowInlinePadding: {
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.075'),
	},
	rowBlockPadding: {
		paddingBlock: token('space.100'),
	},
	row: {
		width: '100%',
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	searchResultRow: {
		borderRadius: token('radius.large'),
	},
	rowTogglePlaceholder: {
		width: '24px',
		height: '24px',
		flexShrink: 0,
	},
	rowDivider: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	spaceChats: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
		backgroundColor: token('elevation.surface'),
	},
	spaceChatItem: {
		paddingInlineStart: token('space.600'),
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	spaceChatItemDivider: {
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	spaceChatContent: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.0'),
	},
	spaceChatIcon: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		width: '32px',
		height: '32px',
	},
	spaceChatItemContent: {
		flexGrow: 1,
		minWidth: '0px',
		marginInlineStart: token('space.negative.100'),
	},
	spaceChatsState: {
		paddingBlock: token('space.200'),
		paddingInlineStart: token('space.600'),
		paddingInlineEnd: token('space.200'),
	},
	searchChatItem: {
		borderRadius: token('radius.large'),
		backgroundColor: token('elevation.surface'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	searchChatItemContent: {
		paddingTop: token('space.025'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.200'),
	},
	searchChatSpaceMetadata: {
		minWidth: '0px',
		maxWidth: '160px',
		flexShrink: 0,
	},
	searchChatSpaceMetadataName: {
		minWidth: '0px',
		flexShrink: 1,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	emojiTile: {
		flexShrink: 0,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '32px',
		height: '32px',
		borderRadius: token('radius.small'),
		backgroundColor: token('color.background.neutral'),
	},
	rowText: {
		flex: '1',
		minWidth: '0px',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.025'),
	},
	rowName: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowMetadata: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	rowButton: {
		flexGrow: 1,
		minWidth: '0px',
		backgroundColor: 'transparent',
		textAlign: 'left',
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	renameForm: {
		gap: token('space.050'),
		backgroundColor: token('elevation.surface'),
	},
	rowMetadataSkeleton: {
		width: '128px',
	},
	noResultsWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		textAlign: 'center',
		height: '100%',
		paddingBottom: token('space.800'),
	},
});
/* eslint-enable @atlaskit/ui-styling-standard/no-unsafe-values */

export type BrowseSpacesFullscreenLayout = 'sidebar' | 'fullscreen';

export type BrowseSpacesFullscreenProps = {
	/** The current cloud / site ID. Required by the spaces fetch + emoji provider. */
	cloudId: string;
	product: string;
	experienceId: string;
	/**
	 * Same params used elsewhere by the spaces UI (forwarded to the
	 * Create Space modal so source icons resolve correctly).
	 */
	assistanceServiceParams?: AssistanceServiceInputParam['assistanceServiceParams'];
	/** Fired when the user clicks a space row. Hosts route to that space's chat view. */
	onSelectSpace: (space: Space) => void;
	/** Fired when the user clicks a chat row rendered inside an expanded space. */
	onSelectSpaceChat?: (chat: RovoNavigationConversation) => void;
	/** Host-local conversations that have been associated with spaces in the current session. */
	localSpaceConversations?: RovoNavigationConversation[];
	/** Space currently selected by the host, used to count the active optimistic chat before BE catches up. */
	activeSpaceId?: string;
	/** Conversation currently selected by the host, used with `activeSpaceId` for narrow optimistic counts. */
	activeConversationId?: string;
	/**
	 * Fired after a space is successfully created via the embedded Create Space
	 * modal. The host owns optimistic store updates — this component does NOT
	 * call `addSpace` itself.
	 */
	onSpaceCreated?: (space: CreateSpaceResponse, emojiId?: string) => void;
	/** Fired when the user clicks the no-spaces empty-state CTA to view recommended spaces. */
	onViewRecommendedSpaces?: () => void;
	/**
	 * Optional chrome row (`←` `≡` `Rovo` `⋯` `X`) — when provided, rendered
	 * as the first child of the wrapper `<Stack>`. Mirrors the non-fullscreen
	 * branch of `ChatConversationListRefresh`. Hosts should pass this in so
	 * the user has back / close / fullscreen-toggle affordances.
	 */
	chatRefreshHeader?: React.ReactNode;
	/**
	 * The same component is used inside the compact Rovo sidebar and the
	 * Townsquare fullscreen route. Sidebar keeps the local panel gutters and
	 * chrome row; fullscreen centers the Figma 800px content column and omits
	 * the sidebar chrome.
	 */
	layout?: BrowseSpacesFullscreenLayout;
};

type SpaceRowProps = {
	space: Space;
	/**
	 * Shared emoji provider — created once by `BrowseSpacesFullscreen` and
	 * passed down so we don't instantiate N providers when rendering N rows
	 * (the provider only depends on `cloudId`, which is the same for every
	 * row in this view).
	 */
	emojiProvider: ReturnType<typeof createEmojiProvider>;
	renameSpace: (spaceId: string, newName: string) => Promise<void>;
	onDeleteSpace: (space: Space) => void;
	onSelectSpace: (space: Space) => void;
	onSelectSpaceChat?: (chat: RovoNavigationConversation) => void;
	localSpaceConversations?: RovoNavigationConversation[];
	activeSpaceId?: string;
	activeConversationId?: string;
	cloudId: string;
	product: string;
	experienceId: string;
	isSearchResult?: boolean;
};

type SearchChatResult = {
	conversation: RovoNavigationConversation;
	space: Space;
};

const getSpaceConversationCount = (space: Space): number => {
	const conversationIds = new Set<string>();
	space.sources?.forEach((source) => {
		source.conversationIds?.forEach((conversationId) => {
			conversationIds.add(conversationId);
		});
	});

	return conversationIds.size;
};

const isEmptySpaceConversation = (conversation: RovoNavigationConversation): boolean =>
	conversation.isEmpty ?? !conversation.lastMessage?.trim();

const isConversationInSpace = ({
	conversation,
	spaceId,
	activeSpaceId,
	activeConversationId,
}: {
	conversation: RovoNavigationConversation;
	spaceId: string;
	activeSpaceId?: string;
	activeConversationId?: string;
}): boolean =>
	conversation.rovoSpaceId === spaceId ||
	(!conversation.rovoSpaceId &&
		activeSpaceId === spaceId &&
		conversation.id === activeConversationId);

const getFetchedSpaceConversationCount = ({
	spaceId,
	fetchedConversationCount,
	fetchedConversations,
	localSpaceConversations = [],
	activeSpaceId,
	activeConversationId,
}: {
	spaceId: string;
	fetchedConversationCount?: number;
	fetchedConversations?: RovoNavigationConversation[];
	localSpaceConversations?: RovoNavigationConversation[];
	activeSpaceId?: string;
	activeConversationId?: string;
}): number | undefined => {
	if (fetchedConversationCount === undefined) {
		return undefined;
	}

	const fetchedConversationIds = new Set(
		fetchedConversations?.flatMap((conversation) =>
			conversation.backendConversationId
				? [conversation.id, conversation.backendConversationId]
				: [conversation.id],
		),
	);
	const localOnlyCount = localSpaceConversations.filter(
		(conversation) =>
			isConversationInSpace({ conversation, spaceId, activeSpaceId, activeConversationId }) &&
			!fetchedConversationIds.has(conversation.id) &&
			(!conversation.backendConversationId ||
				!fetchedConversationIds.has(conversation.backendConversationId)),
	).length;

	return fetchedConversationCount + localOnlyCount;
};

const getLocalSpaceConversations = ({
	spaceId,
	localSpaceConversations = [],
	activeSpaceId,
	activeConversationId,
}: {
	spaceId: string;
	localSpaceConversations?: RovoNavigationConversation[];
	activeSpaceId?: string;
	activeConversationId?: string;
}): RovoNavigationConversation[] =>
	localSpaceConversations
		.filter((conversation) =>
			isConversationInSpace({ conversation, spaceId, activeSpaceId, activeConversationId }),
		)
		.map((conversation) => ({
			...conversation,
			rovoSpaceId: conversation.rovoSpaceId ?? spaceId,
			isEmpty: isEmptySpaceConversation(conversation),
		}));

const mergeFetchedAndLocalSpaceConversations = ({
	spaceId,
	fetchedConversations,
	localSpaceConversations,
	activeSpaceId,
	activeConversationId,
}: {
	spaceId: string;
	fetchedConversations: RovoNavigationConversation[];
	localSpaceConversations?: RovoNavigationConversation[];
	activeSpaceId?: string;
	activeConversationId?: string;
}): RovoNavigationConversation[] => {
	const localConversations = getLocalSpaceConversations({
		spaceId,
		localSpaceConversations,
		activeSpaceId,
		activeConversationId,
	});
	const localById = new Map(
		localConversations.map((conversation) => [conversation.id, conversation]),
	);
	const localByBackendId = new Map(
		localConversations
			.filter((conversation) => Boolean(conversation.backendConversationId))
			.map((conversation) => [conversation.backendConversationId as string, conversation]),
	);
	const consumedLocalIds = new Set<string>();
	const mergedFetchedConversations = fetchedConversations.map((fetchedConversation) => {
		const local =
			localById.get(fetchedConversation.id) ?? localByBackendId.get(fetchedConversation.id);

		if (!local) {
			return fetchedConversation;
		}

		consumedLocalIds.add(local.id);
		const localLastMessage = local.lastMessage?.trim() ? local.lastMessage : undefined;

		return {
			...fetchedConversation,
			name: local.name ?? fetchedConversation.name,
			lastMessage: localLastMessage ?? fetchedConversation.lastMessage,
			lastMessageType: local.lastMessageType ?? fetchedConversation.lastMessageType,
			lastMessageTimestamp: localLastMessage
				? (local.lastMessageTimestamp ?? fetchedConversation.lastMessageTimestamp)
				: fetchedConversation.lastMessageTimestamp,
			lastAgentMessageStatus:
				local.lastAgentMessageStatus ?? fetchedConversation.lastAgentMessageStatus,
			agent: local.agent ?? fetchedConversation.agent,
			readStatus: local.readStatus ?? fetchedConversation.readStatus,
			scopes: local.scopes ?? fetchedConversation.scopes,
			tags: local.tags ?? fetchedConversation.tags,
			currentTask: local.currentTask ?? fetchedConversation.currentTask,
		};
	});
	const localOnlyConversations = localConversations.filter(
		(conversation) => !consumedLocalIds.has(conversation.id),
	);

	return [...localOnlyConversations, ...mergedFetchedConversations];
};

const doesConversationMatchSearch = (
	conversation: RovoNavigationConversation,
	trimmedSearchQuery: string,
): boolean =>
	conversation.name?.toLocaleLowerCase().includes(trimmedSearchQuery.toLocaleLowerCase()) ?? false;

/**
 * Single space row. Mirrors the Spaces Figma row pattern: icon tile, title, and
 * compact chat/source metadata.
 */
const SpaceRow = ({
	space,
	emojiProvider,
	renameSpace,
	onDeleteSpace,
	onSelectSpace,
	onSelectSpaceChat,
	localSpaceConversations,
	activeSpaceId,
	activeConversationId,
	cloudId,
	product,
	experienceId,
	isSearchResult = false,
}: SpaceRowProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);
	const {
		formRef,
		inputRef,
		isEditing,
		localSpaceName,
		hasRenameError,
		isRenaming,
		onCancelRename,
		onRenameBtnClicked,
		onSubmitRename,
	} = useInlineSpaceRename({
		space,
		renameSpace,
	});

	const handleClick = useCallback(() => {
		onSelectSpace(space);
	}, [space, onSelectSpace]);
	const handleToggleChats = useCallback(() => {
		setIsExpanded((current) => !current);
	}, []);

	const sourceCount = space.sources?.length ?? 0;
	const chatsRegionId = `browse-spaces-fullscreen-row-chats-${space.id}`;
	// Prefetch the default conversations page as soon as the Browse spaces row
	// mounts. This gives the collapsed metadata an authoritative totalCount and
	// warms the expanded chat list before the user clicks the row arrow.
	const {
		conversationPage,
		isLoading: isChatsLoading,
		error: spaceChatsError,
	} = useGetSpaceConversations({
		spaceId: space.id,
		cloudId,
		product,
		experienceId,
		enabled: true,
	});
	const fetchedChats = useMemo(
		() => conversationPage?.conversations ?? [],
		[conversationPage?.conversations],
	);
	const chats = useMemo(
		() =>
			mergeFetchedAndLocalSpaceConversations({
				spaceId: space.id,
				fetchedConversations: fetchedChats,
				localSpaceConversations,
				activeSpaceId,
				activeConversationId,
			}),
		[space.id, fetchedChats, localSpaceConversations, activeSpaceId, activeConversationId],
	);
	const fetchedChatCount = getFetchedSpaceConversationCount({
		spaceId: space.id,
		fetchedConversationCount: conversationPage?.totalCount,
		fetchedConversations: fetchedChats,
		localSpaceConversations,
		activeSpaceId,
		activeConversationId,
	});
	const isChatCountLoading = fetchedChatCount === undefined && !spaceChatsError;
	const chatCount =
		fetchedChatCount !== undefined
			? Math.max(fetchedChatCount, chats.length)
			: getSpaceConversationCount(space);
	const chatCountText = formatMessage(messages.chatCount, { count: chatCount });
	const sourceCountText = formatMessage(messages.sourceCount, { count: sourceCount });
	const isWaitingForChats = isExpanded && isChatCountLoading;
	const hasExpandedChatsMismatch = isExpanded && chatCount > 0 && chats.length === 0;
	const shouldShowChatsLoadError =
		!isChatsLoading && !isWaitingForChats && (Boolean(spaceChatsError) || hasExpandedChatsMismatch);
	const shouldShowNoChats =
		!isChatsLoading &&
		!isWaitingForChats &&
		!spaceChatsError &&
		!hasExpandedChatsMismatch &&
		chats.length === 0;
	const shouldRenderChats =
		!isChatsLoading && !isWaitingForChats && !spaceChatsError && !hasExpandedChatsMismatch;

	if (isEditing) {
		return (
			<SpaceRowRenameForm
				spaceId={space.id}
				formRef={formRef}
				inputRef={inputRef}
				formXcss={cx(
					styles.rowLayout,
					styles.rowInlinePadding,
					styles.rowBlockPadding,
					styles.renameForm,
				)}
				hasRenameError={hasRenameError}
				isRenaming={isRenaming}
				localSpaceName={localSpaceName}
				onCancelRename={onCancelRename}
				onSubmitRename={onSubmitRename}
			/>
		);
	}

	return (
		<>
			<SpaceRowActions space={space} onRename={onRenameBtnClicked} onDeleteSpace={onDeleteSpace}>
				{({ rowInteractionProps, actions }) => (
					<Box
						xcss={cx(
							styles.rowLayout,
							styles.rowContainerGap,
							styles.rowInlinePadding,
							styles.row,
							isSearchResult && styles.searchResultRow,
						)}
						{...rowInteractionProps}
						testId={`browse-spaces-fullscreen-row-container-${space.id}`}
					>
						{!isChatCountLoading ? (
							<IconButton
								icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
								appearance="subtle"
								spacing="compact"
								label={formatMessage(messages.spaceChatsToggleLabel, { spaceName: space.name })}
								aria-expanded={isExpanded}
								aria-controls={isExpanded ? chatsRegionId : undefined}
								onClick={handleToggleChats}
								testId={`browse-spaces-fullscreen-row-toggle-${space.id}`}
							/>
						) : (
							<Box
								xcss={styles.rowTogglePlaceholder}
								aria-hidden="true"
								testId={`browse-spaces-fullscreen-row-toggle-placeholder-${space.id}`}
							/>
						)}
						<Pressable
							xcss={cx(
								styles.rowLayout,
								styles.rowContentGap,
								styles.rowBlockPadding,
								styles.rowButton,
							)}
							onClick={handleClick}
							testId={`browse-spaces-fullscreen-row-${space.id}`}
						>
							{space.emoji ? (
								<Box xcss={styles.emojiTile} aria-hidden="true">
									<ResourcedEmoji
										emojiId={{ id: space.emoji, shortName: space.emoji }}
										emojiProvider={emojiProvider}
										fitToHeight={16}
									/>
								</Box>
							) : (
								<IconTile icon={FolderClosedIcon} label="" appearance="gray" size="medium" />
							)}
							<Box xcss={styles.rowText}>
								<Box xcss={styles.rowName}>
									<Text weight="medium">{space.name}</Text>
								</Box>
								{isChatCountLoading ? (
									<Box
										xcss={styles.rowMetadataSkeleton}
										testId={`browse-spaces-fullscreen-row-metadata-skeleton-${space.id}`}
									>
										<Skeleton
											width="100%"
											height={14}
											isShimmering
											borderRadius={token('radius.small')}
										/>
									</Box>
								) : (
									<Box xcss={styles.rowMetadata}>
										<Text size="small" color="color.text.subtlest">
											{chatCountText} &bull; {sourceCountText}
										</Text>
									</Box>
								)}
							</Box>
						</Pressable>
						{actions}
					</Box>
				)}
			</SpaceRowActions>
			{isExpanded && (
				<Box
					id={chatsRegionId}
					xcss={styles.spaceChats}
					role="list"
					testId={`browse-spaces-fullscreen-row-chats-${space.id}`}
				>
					{(isChatsLoading || isWaitingForChats) && (
						<Box xcss={styles.spaceChatsState} role="listitem">
							<Spinner size="medium" label={formatMessage(messages.loadingChatsLabel)} />
						</Box>
					)}
					{shouldShowChatsLoadError && (
						<Box xcss={styles.spaceChatsState} role="listitem">
							<Text color="color.text.danger">{formatMessage(messages.chatsErrorTitle)}</Text>
						</Box>
					)}
					{shouldShowNoChats && (
						<Box xcss={styles.spaceChatsState} role="listitem">
							<Text color="color.text.disabled">{formatMessage(messages.noChatsTitle)}</Text>
						</Box>
					)}
					{shouldRenderChats &&
						chats.map((chat, index) => {
							const chatTitle = chat.name || formatMessage(messages.untitledChat);
							const chatWithTitle = { ...chat, name: chatTitle };

							return (
								<Box
									key={chat.id}
									xcss={cx(styles.spaceChatItem, index > 0 && styles.spaceChatItemDivider)}
									role="listitem"
								>
									<Box xcss={styles.spaceChatContent}>
										<Box xcss={styles.spaceChatIcon} aria-hidden="true">
											<NodeIcon label="" size="medium" color={token('color.icon')} />
										</Box>
										<Box xcss={styles.spaceChatItemContent}>
											<ChatItem
												conversation={chatWithTitle}
												onConversationSelect={() => onSelectSpaceChat?.(chatWithTitle)}
												isSpinnerOverlayConversationIconEnabled={false}
												hideActions
												hasParentHoverBackground
												cloudId={cloudId}
												testId="browse-spaces-fullscreen-space-chat-item"
												ariaLabel={formatMessage(messages.openChatLabel, {
													name: chatTitle,
												})}
											/>
										</Box>
									</Box>
								</Box>
							);
						})}
				</Box>
			)}
		</>
	);
};

export const BrowseSpacesFullscreen = ({
	cloudId,
	product,
	experienceId,
	assistanceServiceParams,
	onSelectSpace,
	onSelectSpaceChat,
	localSpaceConversations,
	activeSpaceId,
	activeConversationId,
	onSpaceCreated,
	onViewRecommendedSpaces,
	chatRefreshHeader,
	layout = 'sidebar',
}: BrowseSpacesFullscreenProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();
	const { fireSpaceSearched } = useSpacesAnalytics();

	// Blank query shows the full list; non-blank query switches to server-side search.
	const [searchQuery, setSearchQuery] = useState('');
	const [isCreateSpaceModalOpen, setIsCreateSpaceModalOpen] = useState(false);
	const [spaceToDelete, setSpaceToDelete] = useState<Space | undefined>(undefined);

	const { deleteSpace } = useDeleteSpace({
		cloudId,
		product,
		experienceId,
	});
	const { renameSpace } = useRenameSpace({
		cloudId,
		product,
		experienceId,
	});

	// Defence-in-depth: skip the fetch entirely when the experiment is off, even
	// though hosts should not render this component in that case.
	const isSpacesFEEnabled = FeatureGates.getExperimentValue(
		'rovo_chat_spaces_experiment',
		'isEnabled',
		false,
	);
	const trimmedSearchQuery = searchQuery.trim();
	const isSearchQueryEmpty = trimmedSearchQuery.length === 0;

	const listResult = useListSpaces({
		cloudId,
		product,
		experienceId,
		skip: !isSpacesFEEnabled,
	});
	const searchResult = useSearchSpaces({
		cloudId,
		product,
		experienceId,
		searchQuery,
		skip: !isSpacesFEEnabled || isSearchQueryEmpty,
	});

	// Fire the screen-view event once on mount. Mirrors `agentsFullPageView`.
	useEffect(() => {
		sendAnalyticsEvent({
			eventType: 'screen',
			name: 'rovoChatBrowseSpaces',
			attributes: {
				touchpointSource: assistanceServiceParams?.touchpointSource,
			},
		});
		// Intentional once-on-mount fire — see also BrowseAgentsFullscreenNew.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Fire one `searched` event per distinct completed search — once results
	// resolve for a non-blank query, not on every keystroke.
	const lastSearchedQueryRef = useRef('');
	useEffect(() => {
		const trimmed = searchQuery.trim();
		if (!trimmed) {
			// Reset so returning to the same term later logs a fresh search.
			lastSearchedQueryRef.current = '';
			return;
		}
		if (searchResult.isLoading || lastSearchedQueryRef.current === trimmed) {
			return;
		}
		lastSearchedQueryRef.current = trimmed;
		fireSpaceSearched({ queryLength: trimmed.length, resultCount: searchResult.spaces?.length });
	}, [searchQuery, searchResult.isLoading, searchResult.spaces, fireSpaceSearched]);

	const openCreateSpaceModal = useCallback(() => {
		setIsCreateSpaceModalOpen(true);
	}, []);

	const closeCreateSpaceModal = useCallback(() => {
		setIsCreateSpaceModalOpen(false);
	}, []);

	const openDeleteSpaceModal = useCallback((space: Space) => {
		setSpaceToDelete(space);
	}, []);

	const closeDeleteSpaceModal = useCallback(() => {
		setSpaceToDelete(undefined);
	}, []);

	// Single shared emoji provider for every row. The provider only depends
	// on `cloudId` (which is constant across this view), so creating it once
	// here avoids instantiating N providers when rendering N rows.
	const sharedEmojiProvider = useMemo(() => createEmojiProvider(cloudId), [cloudId]);

	// Mirror the host-level optimistic-add pattern used in `main.tsx` (line
	// ~638) and `rovo-side-navigation/index.tsx` (which explicitly defers to
	// the host for this very reason). The shared `SpaceStore` is the single
	// source of truth for both the side-nav list and our list (both render via
	// `useListSpaces` → `spaceStore`); pushing the new space into the store
	// makes it appear here AND in the side nav instantly, with no extra
	// network round-trip.
	const { addSpace } = useSpaceStoreActions();

	const handleSpaceCreated = useCallback(
		(space: CreateSpaceResponse, emojiId?: string) => {
			setIsCreateSpaceModalOpen(false);
			addSpace(space, emojiId);
			onSpaceCreated?.(space, emojiId);
		},
		[addSpace, onSpaceCreated],
	);

	const isFullscreenLayout = layout === 'fullscreen';
	const localSpaceConversationsBySpaceId = useMemo(() => {
		const conversationsBySpaceId = new Map<string, RovoNavigationConversation[]>();

		localSpaceConversations?.forEach((conversation) => {
			const conversationSpaceId =
				conversation.rovoSpaceId ??
				(activeSpaceId !== undefined && conversation.id === activeConversationId
					? activeSpaceId
					: undefined);

			if (conversationSpaceId === undefined) {
				return;
			}

			const conversationsForSpace = conversationsBySpaceId.get(conversationSpaceId);
			if (conversationsForSpace !== undefined) {
				conversationsForSpace.push(conversation);
				return;
			}

			conversationsBySpaceId.set(conversationSpaceId, [conversation]);
		});

		return conversationsBySpaceId;
	}, [localSpaceConversations, activeSpaceId, activeConversationId]);
	const listSpaceIds = useMemo(
		() => listResult.spaces.map((space) => space.id),
		[listResult.spaces],
	);
	const {
		conversationsBySpaceId: searchSpaceConversationsBySpaceId,
		isLoading: isSearchChatsLoading,
	} = useSearchSpaceConversations({
		cloudId,
		product,
		experienceId,
		spaceIds: listSpaceIds,
		enabled: !isSearchQueryEmpty,
	});
	const matchingLocalChats = useMemo<SearchChatResult[]>(() => {
		if (isSearchQueryEmpty) {
			return [];
		}

		const results: SearchChatResult[] = [];

		listResult.spaces.forEach((space) => {
			const conversations = mergeFetchedAndLocalSpaceConversations({
				spaceId: space.id,
				fetchedConversations: searchSpaceConversationsBySpaceId.get(space.id) ?? [],
				localSpaceConversations: localSpaceConversationsBySpaceId.get(space.id),
				activeSpaceId,
				activeConversationId,
			});

			conversations.forEach((conversation: RovoNavigationConversation) => {
				if (doesConversationMatchSearch(conversation, trimmedSearchQuery)) {
					results.push({ conversation, space });
				}
			});
		});

		return results;
	}, [
		activeConversationId,
		activeSpaceId,
		isSearchQueryEmpty,
		listResult.spaces,
		localSpaceConversationsBySpaceId,
		searchSpaceConversationsBySpaceId,
		trimmedSearchQuery,
	]);
	const spaces = useMemo(
		() => (isSearchQueryEmpty ? listResult.spaces : searchResult.spaces),
		[isSearchQueryEmpty, listResult.spaces, searchResult.spaces],
	);
	const isLoading = isSearchQueryEmpty
		? listResult.isLoading
		: searchResult.isLoading || listResult.isLoading;
	const error = isSearchQueryEmpty ? listResult.error : searchResult.error;
	const hasResults =
		spaces.length > 0 ||
		matchingLocalChats.length > 0 ||
		(!isSearchQueryEmpty && isSearchChatsLoading);

	const content = (
		<>
			<Box
				xcss={cx(styles.headerContainerBase, !isFullscreenLayout && styles.sidebarHeaderContainer)}
			>
				<Inline xcss={styles.header} alignBlock={isFullscreenLayout ? 'start' : 'center'}>
					<Heading size="large">{formatMessage(messages.pageTitle)}</Heading>
					<Button
						appearance="default"
						spacing="default"
						onClick={openCreateSpaceModal}
						testId="rovo-spaces-browse-spaces-fullscreen-create-space-button"
					>
						{formatMessage(messages.createSpaceButtonLabel)}
					</Button>
				</Inline>

				<Box xcss={styles.searchBarContainer}>
					<SearchInput
						placeholderText={formatMessage(messages.searchPlaceholder)}
						onSearch={setSearchQuery}
						isCompact
					/>
				</Box>
			</Box>

			{isLoading && (
				<Box xcss={styles.spinnerContainer}>
					<Spinner size="medium" label={formatMessage(messages.loadingLabel)} />
				</Box>
			)}

			{!isLoading && error && (
				<Box xcss={styles.spinnerContainer}>
					<Text color="color.text.danger">{formatMessage(messages.errorTitle)}</Text>
				</Box>
			)}

			{!isLoading &&
				!error &&
				!hasResults && (
					// Branch on whether a search query is active so we don't
					// conflate "user has no spaces at all" with "search returned
					// no results".
					<>
						{!isSearchQueryEmpty ? (
							<Stack xcss={styles.noResultsWrapper} alignInline="center" space="space.200">
								<img
									src={noResultIconUrl}
									alt=""
									role="presentation"
									width={160}
									height={160}
									data-testid="rovo-spaces-browse-spaces-fullscreen-no-results-icon"
								/>
								<Stack alignInline="center" space="space.050">
									<Heading size="small">{formatMessage(messages.searchEmptyStateTitle)}</Heading>
									<Text size="large">
										{formatMessage(messages.searchEmptyStateQuery, {
											query: trimmedSearchQuery,
										})}
									</Text>
								</Stack>
							</Stack>
						) : (
							<NoSpacesEmptyState
								isFullscreenLayout={isFullscreenLayout}
								onCreateSpace={openCreateSpaceModal}
								onViewRecommendedSpaces={onViewRecommendedSpaces}
							/>
						)}
					</>
				)}

			{!isLoading && !error && hasResults && (
				<Box
					xcss={cx(
						styles.listContainerBase,
						!isSearchQueryEmpty && styles.searchResultsContainer,
						!isFullscreenLayout && styles.sidebarListContainer,
					)}
					role="list"
					testId="rovo-spaces-browse-spaces-fullscreen-list"
				>
					{spaces.map((space, index) => (
						<Box
							role="listitem"
							key={space.id}
							xcss={!isSearchQueryEmpty ? styles.searchResultItem : undefined}
						>
							<SpaceRow
								space={space}
								emojiProvider={sharedEmojiProvider}
								renameSpace={renameSpace}
								onDeleteSpace={openDeleteSpaceModal}
								onSelectSpace={onSelectSpace}
								onSelectSpaceChat={onSelectSpaceChat}
								localSpaceConversations={localSpaceConversationsBySpaceId.get(space.id)}
								activeSpaceId={activeSpaceId}
								activeConversationId={activeConversationId}
								cloudId={cloudId}
								product={product}
								experienceId={experienceId}
								isSearchResult={!isSearchQueryEmpty}
							/>
							{isSearchQueryEmpty && index < spaces.length - 1 ? (
								<Box
									xcss={styles.rowDivider}
									testId={`browse-spaces-fullscreen-row-divider-${space.id}`}
								/>
							) : null}
						</Box>
					))}
					{matchingLocalChats.map(({ conversation, space }, index) => {
						const chatTitle = conversation.name || formatMessage(messages.untitledChat);
						const chatWithTitle = { ...conversation, name: chatTitle };
						const spaceTitle = space.name.trim();
						const chatAriaLabel = spaceTitle
							? formatMessage(messages.openChatInSpaceLabel, {
									name: chatTitle,
									spaceName: spaceTitle,
								})
							: formatMessage(messages.openChatLabel, {
									name: chatTitle,
								});

						return (
							<Box
								role="listitem"
								key={`${space.id}-${conversation.id}`}
								xcss={styles.searchResultItem}
							>
								<Box xcss={styles.searchChatItem}>
									<Box xcss={styles.searchChatItemContent}>
										<ChatItem
											conversation={chatWithTitle}
											onConversationSelect={() => onSelectSpaceChat?.(chatWithTitle)}
											isSpinnerOverlayConversationIconEnabled={false}
											hideActions
											hasParentHoverBackground
											cloudId={cloudId}
											testId="browse-spaces-fullscreen-search-chat-item"
											ariaLabel={chatAriaLabel}
											footerPrefix={
												spaceTitle ? (
													<Inline
														alignBlock="center"
														space="space.050"
														xcss={styles.searchChatSpaceMetadata}
													>
														<Box as="span" aria-hidden="true">
															<FolderClosedIcon
																label=""
																size="small"
																color={token('color.icon.subtle')}
															/>
														</Box>
														<Box as="span" xcss={styles.searchChatSpaceMetadataName}>
															<Text
																as="span"
																size="small"
																weight="regular"
																maxLines={1}
																align="start"
																color="color.text.subtlest"
															>
																{spaceTitle}
															</Text>
														</Box>
													</Inline>
												) : undefined
											}
										/>
									</Box>
								</Box>
							</Box>
						);
					})}
					{!isSearchQueryEmpty && isSearchChatsLoading ? (
						<Box role="listitem" xcss={styles.spinnerContainer}>
							<Spinner size="small" label={formatMessage(messages.loadingChatsLabel)} />
						</Box>
					) : null}
				</Box>
			)}
		</>
	);

	return (
		<Stack
			xcss={cx(
				styles.wrapper,
				isFullscreenLayout ? styles.fullscreenWrapper : styles.sidebarWrapper,
			)}
			testId="rovo-spaces-browse-spaces-fullscreen"
			alignBlock="start"
		>
			{/*
			 * Chrome row first — mirrors `ConversationListRefresh`'s
			 * non-fullscreen branch (line 190): `{chatRefreshHeader && chatRefreshHeader}`
			 * inside the same `<Stack>` as the rest of the content.
			 * This view is the active sidebar panel after the side nav closes,
			 * so the margin must be the local panel gutter, not a reserved
			 * side-nav rail.
			 */}
			{!isFullscreenLayout && chatRefreshHeader && chatRefreshHeader}
			{isFullscreenLayout ? (
				<Box
					xcss={styles.fullscreenContentContainer}
					testId="rovo-spaces-browse-spaces-fullscreen-content"
				>
					{content}
				</Box>
			) : (
				content
			)}

			<CreateSpaceModal
				isOpen={isCreateSpaceModalOpen}
				onClose={closeCreateSpaceModal}
				cloudId={cloudId}
				assistanceServiceParams={assistanceServiceParams}
				onCreated={handleSpaceCreated}
			/>
			{spaceToDelete ? (
				<DeleteSpaceModal
					space={spaceToDelete}
					isOpen
					onClose={closeDeleteSpaceModal}
					onDeleteConfirm={deleteSpace}
				/>
			) : null}
		</Stack>
	);
};
